import React, { useState, useEffect } from 'react';
import type { MoFamily, InvitationDetails } from './types';
import { formatWon } from '../../utils/number';

interface NegotiationModalProps {
 invitationDetails: InvitationDetails;
 selectedFamily?: MoFamily | null;
 user: any; // from useAppStore
 onClose: () => void;
 onAccepted: () => void; // calls loadFamilies after accept
}

const NegotiationModal: React.FC<NegotiationModalProps> = ({
 invitationDetails,
 selectedFamily,
 user,
 onClose,
 onAccepted,
}) => {
 const [counterOfferForm, setCounterOfferForm] = useState({
   counterSlots: invitationDetails.proposedSlots,
   counterPrice: invitationDetails.proposedPrice,
   counterMessage: ''
 });
 const [counterSlotDelegation, setCounterSlotDelegation] = useState<('none' | 'dedicated' | 'shared')[]>([]);
 const [isModified, setIsModified] = useState(false);
 const [toFamilySlotNumbers, setToFamilySlotNumbers] = useState<number[]>([]);
 const [toFamilySlotsData, setToFamilySlotsData] = useState<any[]>([]);

 // Load TO family slots on mount
 useEffect(() => {
   const loadToFamilySlots = async () => {
     try {
       const slotsResponse = await fetch(`/mofamily/${invitationDetails.toFamilyHash}/slots`, {
         credentials: 'include'
       });
       if (slotsResponse.ok) {
         const slotsData = await slotsResponse.json();
         const actualSlots = slotsData.data || [];
         const slotNumbers = actualSlots.map((slot: any) => slot.slot_number);
         setToFamilySlotNumbers(slotNumbers);

         // Store slots with authorization info
         setToFamilySlotsData(actualSlots);

         // Initialize delegation array
         const newDelegation = new Array(actualSlots.length).fill('none');

         // Map slot numbers to array indices
         if (invitationDetails.dedicatedSlots) {
           invitationDetails.dedicatedSlots.forEach((slotNum: number) => {
             const index = slotNumbers.indexOf(slotNum);
             if (index !== -1) {
               newDelegation[index] = 'dedicated';
             }
           });
         }
         if (invitationDetails.sharedSlots) {
           invitationDetails.sharedSlots.forEach((slotNum: number) => {
             const index = slotNumbers.indexOf(slotNum);
             if (index !== -1) {
               newDelegation[index] = 'shared';
             }
           });
         }

         setCounterSlotDelegation(newDelegation);
         console.log('Negotiation modal slots:', {
           toFamilySlotNumbers: slotNumbers,
           dedicatedSlots: invitationDetails.dedicatedSlots,
           sharedSlots: invitationDetails.sharedSlots,
           newDelegation
         });
       }
     } catch (error) {
       console.error('Error loading TO family slots:', error);
       // Fallback to simple array
       const toFamilyTotalSlots = invitationDetails.toFamilyTotalSlots || 10;
       const newDelegation = new Array(toFamilyTotalSlots).fill('none');
       setCounterSlotDelegation(newDelegation);
     }
   };

   loadToFamilySlots();
 }, [invitationDetails]);

 // Track modification for negotiation modal
 useEffect(() => {
   if (invitationDetails.status === 'pending') {
     const originalSlots = invitationDetails.proposedSlots;
     const originalPrice = invitationDetails.proposedPrice;

     const slotsChanged = counterOfferForm.counterSlots !== originalSlots;
     const priceChanged = counterOfferForm.counterPrice !== originalPrice;
     const messageAdded = counterOfferForm.counterMessage.trim() !== '';

     setIsModified(slotsChanged || priceChanged || messageAdded);
   }
 }, [counterOfferForm, invitationDetails]);

 // Submit counter offer
 const submitCounterOffer = async () => {
   const dedicatedSlots: number[] = [];
   const sharedSlots: number[] = [];

   counterSlotDelegation.forEach((state, index) => {
     const slotNumber = toFamilySlotNumbers.length > index ? toFamilySlotNumbers[index] : index + 1;
     if (state === 'dedicated') dedicatedSlots.push(slotNumber);
     else if (state === 'shared') sharedSlots.push(slotNumber);
   });

   try {
     const response = await fetch(`/mofamily/counter-family-invitation/${invitationDetails.invitationCode}`, {
       method: 'POST',
       headers: { 'Content-Type': 'application/json' },
       credentials: 'include',
       body: JSON.stringify({
         counterPrice: counterOfferForm.counterPrice,
         counterSlots: counterOfferForm.counterSlots,
         dedicatedSlots,
         sharedSlots,
         counterMessage: counterOfferForm.counterMessage
       })
     });

     if (response.ok) {
       alert('역제안이 전송되었습니다.');
       onClose();
     } else {
       const error = await response.json();
       alert(error.error || '역제안 전송에 실패했습니다.');
     }
   } catch (error) {
     alert('네트워크 오류가 발생했습니다.');
   }
 };

 // Accept invitation
 const acceptInvitation = async () => {
   try {
     // Different endpoint for counter-offer acceptance
     const endpoint = invitationDetails.status === 'countered'
       ? `/mofamily/accept-counter-offer/${invitationDetails.invitationCode}`
       : `/mofamily/accept-family-invitation/${invitationDetails.invitationCode}`;

     const response = await fetch(endpoint, {
       method: 'POST',
       credentials: 'include'
     });

     if (response.ok) {
       alert(invitationDetails.status === 'countered' ? '역제안을 수락했습니다.' : '초대를 수락했습니다.');
       onAccepted();
     } else {
       const error = await response.json();
       alert(error.error || '수락에 실패했습니다.');
     }
   } catch (error) {
     alert('네트워크 오류가 발생했습니다.');
   }
 };

 // Reject invitation
 const rejectInvitation = async () => {
   if (!confirm('정말로 이 초대를 거절하시겠습니까?')) return;

   try {
     const response = await fetch(`/mofamily/reject-family-invitation/${invitationDetails.invitationCode}`, {
       method: 'POST',
       credentials: 'include'
     });

     if (response.ok) {
       alert('초대를 거절했습니다.');
       onClose();
     } else {
       const error = await response.json();
       alert(error.error || '초대 거절에 실패했습니다.');
     }
   } catch (error) {
     alert('네트워크 오류가 발생했습니다.');
   }
 };

 // Handle counter slot click
 const handleCounterSlotClick = async (index: number) => {
   const newDelegation = [...counterSlotDelegation];
   const currentState = newDelegation[index];
   const slotNumber = index + 1;

   // 독점으로 변경하려는 경우, 위임 체인 확인
   const familyHash = selectedFamily?.moFamilyHash || invitationDetails.toFamilyHash;
   if (currentState === 'none' && familyHash) {
     try {
       const response = await fetch(`/mofamily/${familyHash}/check-slot-delegation-chain`, {
         method: 'POST',
         headers: { 'Content-Type': 'application/json' },
         credentials: 'include',
         body: JSON.stringify({ slotNumbers: [slotNumber] })
       });

       if (response.ok) {
         const data = await response.json();
         const slotChainInfo = data.data[slotNumber];

         if (slotChainInfo && slotChainInfo.hasSharedInChain) {
           alert(`슬롯 ${slotNumber}번은 위임 체인에서 공유 위임이 있어 독점으로 위임할 수 없습니다. 공유로만 위임 가능합니다.`);
           newDelegation[index] = 'shared';
         } else {
           newDelegation[index] = 'dedicated';
         }
       } else {
         // 체인 확인 실패 시 기본 동작
         newDelegation[index] = 'dedicated';
       }
     } catch (error) {
       console.error('Failed to check delegation chain:', error);
       newDelegation[index] = 'dedicated';
     }
   } else if (currentState === 'dedicated') {
     newDelegation[index] = 'shared';
   } else {
     newDelegation[index] = 'none';
   }

   setCounterSlotDelegation(newDelegation);
   const proposedSlots = newDelegation.filter(s => s !== 'none').length;
   setCounterOfferForm(prev => ({ ...prev, counterSlots: proposedSlots }));
   setIsModified(true);
 };

 return (
   <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
     <div className="bg-white p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
       <h2 className="text-xl font-bold mb-4">협상</h2>

       {/* 제안자 정보 */}
       <div className="bg-gray-50 p-4 mb-4">
         <div className="flex justify-between items-center mb-2">
           <h3 className="font-semibold">제안 정보</h3>
           <span className="text-sm text-gray-600">
             현재 {invitationDetails.status === 'countered' ? '역제안을 검토 중' : '제안을 받음'}
           </span>
         </div>
         <div className="grid grid-cols-2 gap-2 text-sm">
           <p><span className="font-medium">제안자:</span> {invitationDetails.inviterName}</p>
           <p><span className="font-medium">제안 Family:</span> {invitationDetails.fromFamilyName}</p>
           <p><span className="font-medium">수신 Family:</span> {invitationDetails.toFamilyName}</p>
           <p className="col-span-2 mt-2 font-medium text-blue-600">
             {invitationDetails.status === 'countered'
               ? `👤 현재 ${invitationDetails.inviterName}님이 역제안을 검토할 차례입니다`
               : `👤 현재 ${user?.name || user?.email}님이 제안을 검토할 차례입니다`
             }
           </p>
         </div>
       </div>

       {/* 원래 제안 내용 */}
       <div className="bg-blue-50 p-4 mb-4">
         <h3 className="font-semibold mb-2">원래 제안</h3>
         <div className="space-y-2 text-sm">
           <p><span className="font-medium">슬롯 수:</span> {invitationDetails.proposedSlots}개</p>
           <p><span className="font-medium">슬롯당 일일 가격:</span> {formatWon(invitationDetails.proposedPrice)}</p>
           {invitationDetails.dedicatedSlots && invitationDetails.dedicatedSlots.length > 0 && (
             <p><span className="font-medium">독점 슬롯:</span> {invitationDetails.dedicatedSlots.join(', ')}</p>
           )}
           {invitationDetails.sharedSlots && invitationDetails.sharedSlots.length > 0 && (
             <p><span className="font-medium">공유 슬롯:</span> {invitationDetails.sharedSlots.join(', ')}</p>
           )}
           {invitationDetails.message && (
             <div>
               <span className="font-medium">메시지:</span>
               <p className="mt-1 text-gray-700">{invitationDetails.message}</p>
             </div>
           )}
         </div>
       </div>

       {/* 역제안이 있는 경우 표시 */}
       {invitationDetails.status === 'countered' && (
         <div className="bg-yellow-50 p-4 mb-4">
           <h3 className="font-semibold mb-2">역제안 내용</h3>
           <div className="space-y-2 text-sm">
             <p><span className="font-medium">슬롯 수:</span> {invitationDetails.counterSlots}개</p>
             <p><span className="font-medium">슬롯당 일일 가격:</span> {formatWon(invitationDetails.counterPrice)}</p>
             {invitationDetails.counterMessage && (
               <div>
                 <span className="font-medium">메시지:</span>
                 <p className="mt-1 text-gray-700">{invitationDetails.counterMessage}</p>
               </div>
             )}
           </div>
         </div>
       )}

       {/* 역제안 입력 폼 (status가 pending일 때만) */}
       {invitationDetails.status === 'pending' && (
         <div className="border-t pt-4">
           <h3 className="font-semibold mb-4">역제안하기</h3>

           {/* 슬롯 위임 인터페이스 */}
           <div className="mb-4">
             <label className="block text-sm font-medium text-gray-700 mb-2">
               슬롯 위임
               <span className="relative inline-block ml-1 group">
                 <span className="text-blue-500 cursor-help text-xs align-super">?</span>
                 <div className="hidden group-hover:block absolute bottom-full left-0 w-64 p-3 mb-2 text-xs bg-gray-800 text-white z-10">
                   <div className="font-semibold mb-1">슬롯이란?</div>
                   영상 10개가 반복되며 재생되는 스케줄을 가지고 있으면, 10개의 슬롯(구좌)을 가지고 있다고 말합니다.
                   자신이 가진 슬롯에 영상을 넣어 재생할 수 있는 권한을, 다른 모피어에게 위임할 수 있습니다.<br/><br/>
                   이 때, 자신은 더 이상 관리하지 않고, 모든 권한을 넘기는 걸 <span className="text-red-300">독점(Dedicated)</span> 위임이라 하고,
                   자신과 공동으로 관리할 수 있도록, 협상하는 양 쪽이 모두 권한을 가지는 걸 <span className="text-blue-300">공유(Shared)</span> 위임이라 합니다.
                   <div className="absolute -bottom-2 left-4 w-0 h-0 border-l-4 border-r-4 border-t-4 border-l-transparent border-r-transparent border-t-gray-800"></div>
                 </div>
               </span>
             </label>
             <div className="flex flex-wrap gap-1 justify-start">
               {counterSlotDelegation.map((state, index) => {
                 const slotNumber = toFamilySlotNumbers.length > index ? toFamilySlotNumbers[index] : index + 1;
                 const slotData = toFamilySlotsData[index];
                 const hasAuthority = slotData?.has_authority || false;
                 const isDisabled = !hasAuthority;

                 return (
                   <button
                     key={index}
                     onClick={() => !isDisabled && handleCounterSlotClick(index)}
                     disabled={isDisabled}
                     className={`w-8 h-5 border transition-colors ${
                       !hasAuthority
                         ? 'bg-gray-200 border-gray-300 cursor-not-allowed opacity-50'
                         : state === 'none'
                         ? 'bg-white border-gray-300 hover:border-gray-400'
                         : state === 'dedicated'
                         ? 'bg-green-500 border-green-600 text-white text-xs'
                         : 'bg-blue-500 border-blue-600 text-white text-xs'
                     }`}
                     title={
                       !hasAuthority ? '권한 없음 - 이미 다른 모패밀리에 위임됨' :
                       state === 'none' ? '클릭: 독점 위임' :
                       state === 'dedicated' ? '클릭: 공유 위임' :
                       '클릭: 위임 해제'
                     }
                   >
                     {!hasAuthority ? '×' : state === 'none' ? slotNumber : state === 'dedicated' ? 'D' : 'S'}
                   </button>
                 );
               })}
             </div>
             <div className="mt-2 text-sm text-gray-600">
               <span className="inline-block w-3 h-3 bg-green-500 mr-1"></span>독점(Dedicated)
               <span className="inline-block w-3 h-3 bg-blue-500 ml-3 mr-1"></span>공유(Shared)
               <span className="ml-3">선택된 슬롯: {counterOfferForm.counterSlots}개</span>
               <span className="ml-3 text-xs text-gray-500">({invitationDetails.toFamilyName}의 총 {invitationDetails.toFamilyTotalSlots || counterSlotDelegation.length}개 슬롯)</span>
             </div>
           </div>

           {/* 가격 입력 */}
           <div className="mb-4">
             <label className="block text-sm font-medium text-gray-700 mb-2">
               슬롯당 일일 제안 가격 (원)
             </label>
             <input
               type="number"
               min="0"
               step="1000"
               value={counterOfferForm.counterPrice}
               onChange={(e) => {
                 setCounterOfferForm({ ...counterOfferForm, counterPrice: parseInt(e.target.value) || 0 });
                 setIsModified(true);
               }}
               className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500"
             />
           </div>

           {/* 메시지 */}
           <div className="mb-4">
             <label className="block text-sm font-medium text-gray-700 mb-2">
               메시지 (선택사항)
             </label>
             <textarea
               rows={3}
               value={counterOfferForm.counterMessage}
               onChange={(e) => {
                 setCounterOfferForm({ ...counterOfferForm, counterMessage: e.target.value });
                 setIsModified(true);
               }}
               placeholder="역제안에 대한 메시지를 입력하세요..."
               className="w-full px-3 py-2 border focus:outline-none focus:ring-2 focus:ring-blue-500"
             />
           </div>
         </div>
       )}

       {/* 버튼들 */}
       <div className="flex gap-3 justify-center mt-6">
         {invitationDetails.status === 'pending' ? (
           <>
             <button
               onClick={acceptInvitation}
               disabled={isModified}
               className={`px-6 py-3 ${
                 isModified
                   ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                   : 'bg-green-500 hover:bg-green-600 text-white'
               }`}
             >
               수락
             </button>
             <button
               onClick={submitCounterOffer}
               disabled={!isModified || counterOfferForm.counterSlots === 0}
               className={`px-6 py-3 ${
                 !isModified || counterOfferForm.counterSlots === 0
                   ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                   : 'bg-yellow-500 hover:bg-yellow-600 text-white'
               }`}
             >
               역제안
             </button>
             <button
               onClick={rejectInvitation}
               className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white"
             >
               거절
             </button>
           </>
         ) : (
           <>
             <button
               onClick={acceptInvitation}
               className="px-6 py-3 bg-green-500 hover:bg-green-600 text-white"
             >
               역제안 수락
             </button>
             <button
               onClick={rejectInvitation}
               className="px-6 py-3 bg-red-500 hover:bg-red-600 text-white"
             >
               거절
             </button>
           </>
         )}
         <button
           onClick={onClose}
           className="px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white"
         >
           닫기
         </button>
       </div>
     </div>
   </div>
 );
};

export default NegotiationModal;
